import TrinityBondABI from '../abis/TrinityBond.json';
import TrinityOTCABI from '../abis/TrinityOTC.json';
import ERC20ABI from '../abis/MockERC20.json';
import TrinityABI from '../abis/Trinity.json';
import TrinityEntityABI from '../abis/TrinityEntity.json';
import TrinityTreasurerABI from '../abis/TrinityTreasurer.json';
import TrinityProductABI from '../abis/TrinityProduct.json';
import TrinityPropertiesABI from '../abis/TrinityProperties.json';
import NTABI from '../abis/NT.json';

import { Tag, Bank, Handshake, Scroll, ChartLineUp, Gift, ChartBar, Buildings, Storefront, FirstAid, Certificate, Target } from '@phosphor-icons/react';

export const CHAIN_ID = 1337 // Local development network

export const ADDRESSES = {
            FED: '0x5FbDB2315678afecb367f032d93F642f64180aa3',
            TRINITY_TOKEN: '0xCf7Ed3AccA5a467e9e704C703E8D87F634fB0Fc9',
            COUPON_TOKEN: '0xA51c1fc2f0D1a1b8494Ed1FE312d7C3a78Ed91C0',
            TBILL: '0xe7f1725E7734CE288F8367e1Bb143E90bb3F0512',
            PDAI: '0x9fE46736679d2D9a65F0992F2272dE9f3c7fa6e0',
            TRINITY_BOND: '0x59b670e9fA9D0A427751Af201D676719a970857b',
            TRINITY_MASTER: '0x0B306BF915C4d645ff596e518fAf3F9669b97016',
            TRINITY_TREASURER: '0xE6E340D132b5f46d1e472DebcD681B2aBc16e57E',
            MOCK_PRICE_ORACLE: '0x959922bE3CAee4b8Cd9a407cc3ac1C251C2007B1',
            MOCK_PULSEX_ROUTER: '0xc6e7DF5E7b4f2A278906862b61205850344D4e7d',
            NT: '0xB7f8BC63BbcaD18155201308C8f3540b07f84F5e'
        } as const;

export const CONFIG = {
  TRINITY_BOND_ABI: TrinityBondABI,
  TRINITY_OTC_ABI: TrinityOTCABI,
  ERC20_ABI: ERC20ABI,
  TRINITY_ABI: TrinityABI,
  TRINITY_ENTITY_ABI: TrinityEntityABI,
  TRINITY_TREASURER_ABI: TrinityTreasurerABI,
  TRINITY_PRODUCT_ABI: TrinityProductABI,
  TRINITY_PROPERTIES_ABI: TrinityPropertiesABI,
  TRINITY_ITEM_ABI: TrinityProductABI,
  NT_ABI: NTABI.abi,
  TRINITY_ADDRESS: ADDRESSES.TRINITY_MASTER as `0x${string}`,
};

export type TokenAddress = typeof ADDRESSES[keyof typeof ADDRESSES];

export const TOKEN_NAMES: Record<TokenAddress, string> = {
  [ADDRESSES.TRINITY_BOND]: 'Trinity Bond',
  [ADDRESSES.FED]: 'Teh F㉭D',
  [ADDRESSES.TRINITY_TOKEN]: '†rinity',
  [ADDRESSES.COUPON_TOKEN]: 'Sain†',
  [ADDRESSES.TBILL]: 'Treasury Bill',
  [ADDRESSES.PDAI]: 'pDai',
  [ADDRESSES.MOCK_PRICE_ORACLE]: 'Mock Price Oracle',
  [ADDRESSES.MOCK_PULSEX_ROUTER]: 'Mock PulseX Router',
  [ADDRESSES.TRINITY_MASTER]: 'Trinity Master',
  [ADDRESSES.TRINITY_TREASURER]: 'Trinity Treasurer',
  [ADDRESSES.NT]: 'NT Token'
} as const;

export const TOKEN_SYMBOLS: Record<TokenAddress, string> = {
  [ADDRESSES.TRINITY_BOND]: 'Trinity Bond',
  [ADDRESSES.FED]: 'F㉭D',
  [ADDRESSES.TRINITY_TOKEN]: '†',
  [ADDRESSES.COUPON_TOKEN]: 'S†',
  [ADDRESSES.TBILL]: 'TBILL',
  [ADDRESSES.PDAI]: 'pDai',
  [ADDRESSES.MOCK_PRICE_ORACLE]: 'Mock Price Oracle',
  [ADDRESSES.MOCK_PULSEX_ROUTER]: 'Mock PulseX Router',
  [ADDRESSES.TRINITY_MASTER]: 'TRNTY-M',
  [ADDRESSES.TRINITY_TREASURER]: 'TRNTY-T',
  [ADDRESSES.NT]: 'NT'
} as const;

export const TOKEN_ICON_SYMBOLS: Record<TokenAddress, string> = {
  [ADDRESSES.TRINITY_BOND]: 'Trinity Bond',
  [ADDRESSES.FED]: '㉭',
  [ADDRESSES.TRINITY_TOKEN]: '†',
  [ADDRESSES.COUPON_TOKEN]: 'S†',
  [ADDRESSES.TBILL]: 'TBILL$',
  [ADDRESSES.PDAI]: 'pDai',
  [ADDRESSES.MOCK_PRICE_ORACLE]: 'Mock Price Oracle',
  [ADDRESSES.MOCK_PULSEX_ROUTER]: 'Mock PulseX Router',
  [ADDRESSES.TRINITY_MASTER]: 'TM',
  [ADDRESSES.TRINITY_TREASURER]: 'TT',
  [ADDRESSES.NT]: 'NT'
} as const;

export const TOKEN_DECIMALS: Record<TokenAddress, number> = {
  [ADDRESSES.TRINITY_BOND]: 18,
  [ADDRESSES.FED]: 18,
  [ADDRESSES.TRINITY_TOKEN]: 18,
  [ADDRESSES.COUPON_TOKEN]: 18,
  [ADDRESSES.TBILL]: 18,
  [ADDRESSES.PDAI]: 18,
  [ADDRESSES.MOCK_PRICE_ORACLE]: 18,
  [ADDRESSES.MOCK_PULSEX_ROUTER]: 18,
  [ADDRESSES.TRINITY_MASTER]: 18,
  [ADDRESSES.TRINITY_TREASURER]: 18,
  [ADDRESSES.NT]: 18
} as const;

export const SUPPORTED_ASSETS = [ADDRESSES.FED, ADDRESSES.TBILL, ADDRESSES.PDAI] as const;

export const NETWORK_CONFIG = {
  id: CHAIN_ID,
  name: 'Local Network',
  network: 'localhost',
  nativeCurrency: {
    decimals: 18,
    name: 'Ethereum',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: {
      http: ['http://localhost:8545'],
    },
    public: {
      http: ['http://localhost:8545'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Local Explorer',
      url: 'http://localhost:8545',
    },
  },
  testnet: true,
} as const 

interface FinancialProduct {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  enabled: boolean;
  address?: `0x${string}`;
}

export const FINANCIAL_PRODUCTS: FinancialProduct[] = [
  {
    id: 'bonds',
    name: 'Be Your Own Bonds',
    description: 'Create a staking contract with customizable terms and conditions.',
    icon: Tag,
    enabled: true
  },
  {
    id: 'trusts',
    name: 'Be Your Own Trust',
    description: 'Establish secure trust funds with automated distribution.',
    icon: Bank,
    enabled: false
  },
  {
    id: 'good-faith-agreement',
    name: 'Good Faith Agreement',
    description: 'Create binding good faith agreements with customizable terms.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'wills',
    name: 'Will & Testament',
    description: 'Create and manage digital wills with automated execution.',
    icon: Scroll,
    enabled: false
  },
  {
    id: 'bets',
    name: "Gentlemen's Bet",
    description: 'Create and manage friendly wagers with automated settlement.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'gifts',
    name: 'Gift',
    description: 'Create time-locked gift contracts.',
    icon: Gift,
    enabled: false
  },
  {
    id: 'otc',
    name: 'OTC',
    description: 'Over The Counter Agreement.',
    icon: ChartBar,
    enabled: true
  },
  {
    id: 'bet',
    name: 'Betting Platform',
    description: 'Create a betting platform with odds and oracle finalization.',
    icon: Target,
    enabled: true
  },
  {
    id: 'joint-venture',
    name: 'Joint Venture',
    description: 'Create a joint venture agreement.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'liquidity-pool',
    name: 'Liquidity Pool',
    description: 'Create a liquidity pool agreement.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'lending-pool',
    name: 'Lending Pool',
    description: 'Create a lending pool agreement.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'sacpool',
    name: 'Sacrifice Pool',
    description: 'Create a sacrifice pool agreement.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'fundraiser',
    name: 'Fundraise',
    description: 'Create a fundraise agreement.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'sacrifice',
    name: 'Sacrifice',
    description: 'Create a sacrifice agreement.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'movie-theater',
    name: 'Be Your Own Movie Theater',
    description: 'Movie theater.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'book-store',
    name: 'Be Your Own Book Store',
    description: 'Book store.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'music-store',
    name: 'Be Your Own Music Store',
    description: 'Music store.',
    icon: Handshake,
    enabled: false
  },
  {
    id: 'art-gallery',
    name: 'Be Your Own Art Gallery',
    description: 'Art gallery.',
    icon: Handshake,
    enabled: false
  },



] as const;

export const ENTITY_TYPE_ICONS = {
  'Bank': Bank,
  'Store': Storefront,
  'Hospital': FirstAid,
  'Bond Management': Certificate,
  'Unknown Type': Buildings
} as const;